import { dehydrate, hydrate, QueryClient } from "@tanstack/react-query";
import { createRouter as createReactRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { routeTree } from "./routeTree.gen";
import { getCB } from "./data/cb-handler";

let clientQueryClient: QueryClient | undefined;

export function createRouter(
  domainName: string,
  clientScript: string,
) {
  const defaultQueryOptions = {
    queries: {
      staleTime: () => (getCB() ? 0 : 1000 * 60 * 5),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: true,
      retryDelay: 1000,
    },
  };

  const queryClient = import.meta.env.SSR
    ? new QueryClient({
      defaultOptions: defaultQueryOptions,
    })
    : (clientQueryClient ??= new QueryClient({
      defaultOptions: defaultQueryOptions,
    }));

  // if queryClient is created inside it will be per request, we want shared instance
  // so that we get nicely cached client
  const router = createReactRouter({
    routeTree,
    context: {
      queryClient,
      domainName,
      title: "",
      clientScript,
    },
    defaultPreload: import.meta.env.SSR ? false : false,
    scrollRestoration: true,
    // On the server, dehydrate the loader client so the router
    // can serialize it and send it to the client for us
    dehydrate: () => {
      return {
        queryClient: dehydrate(queryClient),
        domainName: domainName,
        title: "",
        clientScript,
      };
    },
    // On the client, hydrate the loader client with the data
    // we dehydrated on the server
    hydrate: (dehydrated) => {
      hydrate(queryClient, dehydrated.queryClient);
    },
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    wrapQueryClient: true,
  });
  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
